import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import Cookies from 'js-cookie';

// Helper to get userId from cookies
const getUserId = () => {
  return Cookies.get('artote_user_id');
};

// Fetch all products
export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  async ({ page = 1, limit = 100, category = '', search = '' }, { rejectWithValue }) => {
    try {
      const userId = getUserId();
      const params = { page, limit };
      
      if (userId) params.userId = userId;
      if (category) params.category = category;
      if (search) params.search = search;

      const response = await axiosInstance.get('/product/list', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

// Fetch featured products
export const fetchFeaturedProducts = createAsyncThunk(
  'product/fetchFeaturedProducts',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const userId = getUserId();
      const params = { page, limit };
      
      if (userId) params.userId = userId;
      if (search) params.search = search;

      const response = await axiosInstance.get('/product/featured/products', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured products');
    }
  }
);

// Fetch product detail
export const fetchProductDetail = createAsyncThunk(
  'product/fetchProductDetail',
  async (productId, { rejectWithValue }) => {
    try {
      const userId = getUserId();
      const params = {};
      
      // Add userId to params if user is logged in
      if (userId) params.userId = userId;

      const response = await axiosInstance.get(`/product/detail/${productId}`, { params });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details');
    }
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState: {
    products: [],
    featuredProducts: [],
    currentProduct: null,
    totalCount: 0,
    featuredTotalCount: 0,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    updateProductWishlistStatus: (state, action) => {
      const { productId, isWishlist } = action.payload;
      
      // Update in products array
      const product = state.products.find(p => p._id === productId);
      if (product) {
        product.isWishlist = isWishlist;
      }
      
      // Update in featured products array
      const featuredProduct = state.featuredProducts.find(p => p._id === productId);
      if (featuredProduct) {
        featuredProduct.isWishlist = isWishlist;
      }
      
      // Update current product if it matches
      if (state.currentProduct && state.currentProduct._id === productId) {
        state.currentProduct.isWishlist = isWishlist;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch featured products
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredProducts = action.payload.data || [];
        state.featuredTotalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch product detail
      .addCase(fetchProductDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearCurrentProduct, updateProductWishlistStatus } = productSlice.actions;
export default productSlice.reducer;
